import {
  ALL_EGG_PATTERNS,
  type AlchemyType,
  type Genome,
} from '@/domain/genetics/types';
import type { BiomeId } from '@/domain/world/types';
import { hashCode, mulberry32, type Rng } from '@/domain/world/rng';
import { ecologyForBiome, type WeightedValue } from './biome-ecology';

export interface GenerateWildGenomeInput {
  biome: BiomeId;
  seed: string;
}

export function generateWildGenome(input: GenerateWildGenomeInput): Genome {
  const rng = mulberry32(hashCode(`${input.biome}:${input.seed}`));
  const ecology = ecologyForBiome(input.biome);
  const typeCountRoll = rng();
  const typeCount = typeCountRoll < 0.72 ? 1 : typeCountRoll < 0.94 ? 2 : 3;
  const primaryTypes = pickUnique(ecology.types, typeCount, rng);
  const hueRange = weightedPick(
    ecology.hueRanges.map((range) => ({ value: range, weight: range.weight })),
    rng,
  );

  return {
    body: weightedPick(ecology.bodies, rng),
    eyes: 'basic',
    mouth: 'basic',
    ears: weightedPick(ecology.ears, rng),
    horns: weightedPick(ecology.horns, rng),
    tail: weightedPick(ecology.tails, rng),
    markings: weightedPick(ecology.markings, rng),
    paletteHue: randomInt(hueRange.min, hueRange.max, rng),
    bodyPredisposition: randomInt(0, 100, rng),
    eggPattern: ALL_EGG_PATTERNS[Math.floor(rng() * ALL_EGG_PATTERNS.length)]!,
    primaryTypes,
    modifiers: [],
    inheritedTraits: [],
  };
}

function weightedPick<T>(entries: readonly WeightedValue<T>[], rng: Rng): T {
  const total = entries.reduce((sum, entry) => sum + entry.weight, 0);
  let cursor = rng() * total;
  for (const entry of entries) {
    cursor -= entry.weight;
    if (cursor < 0) return entry.value;
  }
  return entries[entries.length - 1]!.value;
}

function pickUnique(entries: readonly WeightedValue<AlchemyType>[], count: number, rng: Rng): AlchemyType[] {
  const pool = [...entries];
  const picked: AlchemyType[] = [];
  while (picked.length < count && pool.length > 0) {
    const value = weightedPick(pool, rng);
    picked.push(value);
    pool.splice(pool.findIndex((entry) => entry.value === value), 1);
  }
  return picked;
}

function randomInt(min: number, max: number, rng: Rng): number {
  return min + Math.floor(rng() * (max - min + 1));
}
